import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import type { MouseEvent } from 'react';
import { useTabletLayoutAndUp } from '../utils';
import { CancelButton } from './CancelButton';
import { DeleteButton } from './DeleteButton';
import { SaveButton } from './SaveButton';
import type { StandardButtonProps } from './StandardButton';

/**
 * Cancel/confirm button pair for dialog footers. Use inside an existing dialog
 * shell when the parent owns title, content, and open state — e.g. nested in
 * `RegularDialog.actions` or scrollable content.
 *
 * For a complete modal with shell + footer, use `CancelConfirmDialog` instead.
 */
export interface CancelConfirmActionsProps {
  cancelText: string;
  confirmText: string;
  onClickCancel: (e: MouseEvent<HTMLButtonElement>) => void;
  onClickConfirm: (e: MouseEvent<HTMLButtonElement>) => void;
  onLoadingConfirm?: boolean;
  disabledConfirm?: boolean;
  confirmColor?: StandardButtonProps['color'];
  confirmVariant?: 'save' | 'delete';
  sx?: SxProps<Theme>;
}

export const CancelConfirmActions = ({
  cancelText,
  confirmText,
  onClickCancel,
  onClickConfirm,
  onLoadingConfirm,
  disabledConfirm,
  confirmColor,
  confirmVariant = 'save',
  sx,
}: CancelConfirmActionsProps) => {
  const tabletLayout = useTabletLayoutAndUp();
  const responsiveLayout = tabletLayout
    ? {
        flexWrap: 'wrap-reverse',
        flexDirection: 'row',
        minWidth: '400px',
      }
    : {
        flexWrap: 'initial',
        flexDirection: 'column-reverse',
        alignItems: 'stretch',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
      };

  const responsiveButtons = tabletLayout ? 'auto' : '100%';

  const confirmProps = {
    text: confirmText,
    onClick: onClickConfirm,
    loading: onLoadingConfirm,
    sx: { width: responsiveButtons },
    disabled: disabledConfirm,
    color: confirmColor,
    autoFocus: true,
  };

  return (
    <Stack
      sx={[
        {
          ...responsiveLayout,
          justifyContent: 'flex-end',
          mt: 2,
          gap: 1,
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      <CancelButton
        text={cancelText}
        onClick={onClickCancel}
        sx={{ width: responsiveButtons }}
      />
      {confirmVariant === 'delete' ? (
        <DeleteButton {...confirmProps} color={confirmColor || 'error'} />
      ) : (
        <SaveButton {...confirmProps} />
      )}
    </Stack>
  );
};
